import { getSession, refreshSession } from '../services/AuthService';
import { getCoreApiUrl, hasSameOriginApiProxy } from '../config/deploymentConfig';
import { logWarning } from '../services/LoggingService';

/**
 * Shared API Client Utility
 * Centralized request handling with authentication, deduplication, and error handling
 */

// Request deduplication map for in-flight requests
const pendingRequests = new Map();

// Core API base URL — when set, calls go directly to the core service
// (bypassing the Express/nginx proxy for lower latency)
const CORE_API_BASE_URL = getCoreApiUrl();

// Set once a direct cross-origin call has failed at the network level and the
// same-origin /api proxy answered instead. Some corporate proxies drop the CORS
// preflight (OPTIONS) that every direct call needs; the same-origin route needs
// no preflight. Remembered for the page lifetime so each later call does not
// pay for a failed direct attempt first.
let useSameOriginProxy = false;

/**
 * Error raised when a request never produced an HTTP response: the browser is
 * offline, DNS failed, or a firewall/proxy blocked the request or its CORS
 * preflight. Distinct from an HTTP error status, which the API returned itself.
 */
export class NetworkError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export const isNetworkError = (error) => error instanceof NetworkError;

// fetch() rejects with a TypeError (and nothing else) when no response arrived.
const isFetchNetworkFailure = (error) => error instanceof TypeError;

// Only requests without side effects may be re-sent. A failed fetch does not
// say whether the request reached the API, so re-sending a POST/PUT/DELETE
// could apply the mutation twice.
const isSafeToRetry = (method) => method === 'GET' || method === 'HEAD';

// A same-origin proxy that cannot reach the core service answers with a
// gateway status (Express and the dev proxy send 502, nginx 502/504) rather
// than rejecting fetch(). Such a response says nothing about the direct route.
const isGatewayFailure = (response) => [502, 503, 504].includes(response.status);

const describeNetworkFailure = () => {
  let target = 'the MemBrowse API';
  if (CORE_API_BASE_URL) {
    try {
      target = `the MemBrowse API at ${new URL(CORE_API_BASE_URL).host}`;
    } catch {
      // Keep the generic wording if the configured URL cannot be parsed
    }
  }
  return `Could not reach ${target}. Check your network connection; `
    + 'a corporate firewall or proxy may be blocking these requests.';
};

/**
 * Resolve an API endpoint to a full URL.
 * When CORE_API_BASE_URL is set (cloud deployment), strips the /api prefix
 * and prepends the base URL for direct calls to the core service.
 * When empty (on-prem), returns the endpoint as-is for proxy routing.
 * @param {string} endpoint - API endpoint (e.g. '/api/projects')
 * @returns {string} Resolved URL
 */
const resolveEndpoint = (endpoint) => {
  if (CORE_API_BASE_URL) {
    const path = endpoint.startsWith('/api') ? endpoint.slice(4) : endpoint;
    return `${CORE_API_BASE_URL}${path}`;
  }
  return endpoint;
};

/**
 * Cache-busting value for a request: a timestamp for GET requests, null otherwise
 * @param {string} method - HTTP method
 * @returns {number|null}
 */
const cacheBusterFor = (method) => (method === 'GET' ? Date.now() : null);

/**
 * Add cache-busting query parameter to URL
 * @param {string} url - Original URL
 * @param {number|null} cacheBuster - Value from cacheBusterFor(), or null to leave the URL alone
 * @returns {string} URL with cache-busting parameter if one was given
 */
const addCacheBusting = (url, cacheBuster) => {
  if (cacheBuster === null) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_=${cacheBuster}`;
};

/**
 * Send a request to the core service, falling back to the same-origin /api
 * proxy when the direct cross-origin call fails at the network level.
 *
 * Only a network failure (no response at all) on a safe method (GET/HEAD)
 * triggers the fallback; HTTP error statuses are returned to the caller
 * untouched, and a mutating request is never re-sent. Once the proxy has
 * been chosen, every later request (including mutations) is sent through
 * it from the start, so no mutation is ever replayed. When neither route
 * produces a response, a NetworkError with a user-readable message is thrown.
 * @param {string} endpoint - API endpoint (relative, e.g. '/api/projects')
 * @param {string} method - HTTP method
 * @param {Object} init - fetch() init object (headers, body, ...)
 * @returns {Promise<Response>} Raw fetch response
 */
const fetchCoreService = async (endpoint, method, init) => {
  const directUrl = resolveEndpoint(endpoint);
  const proxyUrl = endpoint;
  const canFallBack = hasSameOriginApiProxy() && directUrl !== proxyUrl;
  const cacheBuster = cacheBusterFor(method);

  const firstUrl = canFallBack && useSameOriginProxy ? proxyUrl : directUrl;
  try {
    const response = await fetch(addCacheBusting(firstUrl, cacheBuster), init);
    if (firstUrl === proxyUrl && isGatewayFailure(response)) {
      // The proxy itself is failing; let the next request try the direct route.
      useSameOriginProxy = false;
    }
    return response;
  } catch (error) {
    if (!isFetchNetworkFailure(error)) {
      throw error;
    }
    if (!canFallBack || firstUrl === proxyUrl) {
      // Nothing else to try. If the proxy was the route that failed, let the
      // next request start over with a direct call.
      useSameOriginProxy = false;
      throw new NetworkError(describeNetworkFailure(), error);
    }
    if (!isSafeToRetry(method)) {
      throw new NetworkError(describeNetworkFailure(), error);
    }
  }

  logWarning(
    `Direct request to ${directUrl} failed before a response arrived; retrying via same-origin proxy`,
    null,
    'apiClient'
  );
  try {
    const response = await fetch(addCacheBusting(proxyUrl, cacheBuster), init);
    // Stick to the proxy only when it actually reached the core service
    useSameOriginProxy = !isGatewayFailure(response);
    return response;
  } catch (error) {
    if (!isFetchNetworkFailure(error)) {
      throw error;
    }
    throw new NetworkError(describeNetworkFailure(), error);
  }
};

/**
 * Create authenticated API request with deduplication
 * @param {string} endpoint - API endpoint (relative URL)
 * @param {Object} options - Fetch options
 * @param {number} retryCount - Current retry count for token refresh
 * @returns {Promise<Object>} Response data
 */
export const coreServiceRequest = async (endpoint, options = {}, retryCount = 0) => {
  const session = await getSession();
  if (!session) {
    // getSession() already attempts a proactive refresh if the token is expired,
    // so if it returns null the user is truly unauthenticated.
    window.location.href = '/signin';
    throw new Error('No active session');
  }

  const method = options.method || 'GET';

  // Create cache key for request deduplication. GET keys carry the same
  // per-request timestamp the URL is cache-busted with, so only requests
  // issued in the same millisecond are deduplicated.
  const cacheKey = `${method}-${addCacheBusting(endpoint, cacheBusterFor(method))}-${JSON.stringify(options.body || {})}`;

  // Return existing promise if same request is already in flight
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  // Create the actual request promise
  const requestPromise = (async () => {
    const response = await fetchCoreService(endpoint, method, {
      ...options,
      cache: 'no-store',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        ...options.headers,
      },
    });

    if (!response.ok) {
      // Handle 401 Unauthorized - token might be expired
      if (response.status === 401 && retryCount === 0) {
        // Try to refresh the token
        const newSession = await refreshSession();
        if (newSession) {
          // Clear the pending request to force a new one with fresh token
          pendingRequests.delete(cacheKey);
          // Retry with the new token
          return coreServiceRequest(endpoint, options, 1);
        } else {
          // Refresh failed, redirect to signin
          window.location.href = '/signin';
          throw new Error('Session expired and refresh failed');
        }
      }
      
      // Try to parse error response for standardized format
      try {
        const errorData = await response.json();
        // Backend uses success: false for errors
        if (errorData.success === false && errorData.error) {
          throw new Error(errorData.error);
        }
        // Also support legacy status: 'error' format
        if (errorData.status === 'error' && errorData.error) {
          throw new Error(`${errorData.type || 'Error'}: ${errorData.error}`);
        }
      } catch (parseError) {
        // If error is already an Error object (thrown above), re-throw it
        if (parseError instanceof Error && parseError.message !== 'Core service error: 500 INTERNAL SERVER ERROR') {
          throw parseError;
        }
        // Fall back to status text if JSON parsing fails
      }
      throw new Error(`Core service error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Check for error status in successful HTTP responses
    if (data.status === 'error') {
      throw new Error(`${data.type || 'Error'}: ${data.error}`);
    }

    return data;
  })();

  // Store the promise for deduplication
  pendingRequests.set(cacheKey, requestPromise);

  // Clean up after request completes (success or failure)
  // The .catch() suppresses the unhandled rejection from the .finally() chain -
  // the actual rejection is handled by the caller of the original requestPromise
  requestPromise.finally(() => {
    pendingRequests.delete(cacheKey);
  }).catch(() => {});

  return requestPromise;
};

/**
 * Create public (unauthenticated) API request for public endpoints
 * @param {string} endpoint - API endpoint (relative URL)
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
export const publicCoreServiceRequest = async (endpoint, options = {}) => {
  const method = options.method || 'GET';

  // Make request without authentication headers
  const response = await fetchCoreService(endpoint, method, {
    ...options,
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      ...options.headers,
    },
  });

  if (!response.ok) {
    // Try to parse error response for standardized format
    try {
      const errorData = await response.json();
      // Backend uses success: false for errors
      if (errorData.success === false && errorData.error) {
        throw new Error(errorData.error);
      }
      // Also support legacy status: 'error' format
      if (errorData.status === 'error' && errorData.error) {
        throw new Error(`${errorData.type || 'Error'}: ${errorData.error}`);
      }
    } catch (parseError) {
      // If error is already an Error object (thrown above), re-throw it
      if (parseError instanceof Error && parseError.message !== 'Core service error: 500 INTERNAL SERVER ERROR') {
        throw parseError;
      }
      // Fall back to status text if JSON parsing fails
    }
    throw new Error(`Core service error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  // Check for error status in successful HTTP responses
  if (data.success === false && data.error) {
    throw new Error(data.error);
  }
  if (data.status === 'error') {
    throw new Error(`${data.type || 'Error'}: ${data.error}`);
  }

  return data;
};

/**
 * Clear all pending requests (useful for cleanup on unmount or cache invalidation)
 */
export const clearPendingRequests = () => {
  pendingRequests.clear();
};